import { useState } from "react";
import { CloudOff, LogOut, RotateCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

/**
 * O que aparece quando o serviço de autenticação não responde.
 *
 * O QUE ESTAVA NO LUGAR DISTO
 * ---------------------------
 *
 * Um spinner, para sempre. Com o token expirado e o serviço de autenticação
 * fora do ar, a renovação da sessão ficava pendurada — nem sucesso, nem erro — e
 * a tela ficava em branco girando. Quem via não tinha como saber se era a
 * internet, o computador, a conta ou o sistema.
 *
 * Espera sem fim é a pior forma de erro: não dá nem para saber que houve um.
 * Mesmo princípio que já valia para o service worker — requisição que falhou não
 * pode chegar à tela parecendo que ainda está a caminho.
 *
 * AS DUAS SAÍDAS, E POR QUE AMBAS
 * -------------------------------
 *
 * "Tentar de novo" serve para a queda passageira. "Sair da conta" serve para o
 * caso em que a sessão local é que está ruim: apaga o que está guardado no
 * navegador e leva ao login. Sem ela, quem tiver um token quebrado fica preso
 * mesmo depois de o serviço voltar.
 */
export function AutenticacaoIndisponivel() {
  const [saindo, setSaindo] = useState(false);

  async function limparSessao() {
    setSaindo(true);
    // `signOut` tenta avisar o servidor e pode falhar justamente porque ele está
    // fora. O que importa aqui é o lado local — por isso o try/catch e a limpeza
    // manual do que sobrar.
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      /* segue */
    }
    try {
      for (const chave of Object.keys(localStorage)) {
        if (chave.startsWith("sb-")) localStorage.removeItem(chave);
      }
    } catch {
      /* segue */
    }
    window.location.href = "/login";
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto rounded-full bg-muted p-3 w-fit mb-4">
          <CloudOff className="h-6 w-6 text-muted-foreground" aria-hidden />
        </div>

        <h1 className="text-lg font-semibold text-foreground">
          Não conseguimos verificar sua sessão
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          O serviço que confirma quem está conectado não respondeu. Seus dados
          estão a salvo — é a verificação de acesso que está indisponível.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={() => window.location.reload()} className="gap-1.5">
            <RotateCw className="h-3.5 w-3.5" aria-hidden />
            Tentar de novo
          </Button>
          <Button variant="ghost" onClick={limparSessao} disabled={saindo} className="gap-1.5">
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            {saindo ? "Saindo…" : "Sair da conta"}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-6">
          Se continuar assim por mais alguns minutos, o problema é do nosso lado e
          já estamos vendo.
        </p>
      </div>
    </div>
  );
}
